import IReactSelectValue from "../../../../interfaces/IReactSelectValue";
import { FilterOptions, Floor } from "../../interfaces/filterOptions";
import ReservationInsightsFilter from "../../interfaces/reservationInsightsFilter";

export default class FilterUtils {

    // The Objects in FilterOptions (Room/Workplace) have some UI only properties to link them to their parent objects
    // Fill the properties that make iterating over the objects easier
    public static enrichFilterOptions(filterOptions: FilterOptions): void {

        const floorsByVenueId: Record<string, Floor[]> = {};
        const departmentsByFloorId: Record<string, Set<string>> = {};
        const floorVenueMap: Record<string, string> = {};

        // Iterate over all rooms to link floor IDs to venue IDs and ensure floorId is set
        filterOptions.rooms.forEach(room => {
            if (!room.floorId || !floorVenueMap[room.floorId]) {
                const floor = filterOptions.floors.find(f => f.venueId === room.venueId);
                if (floor) {
                    room.floorId = floor.id;
                    floorVenueMap[room.floorId] = room.venueId;
                }
            }
        });

        // Gather all floors by venue ID
        filterOptions.floors.forEach(floor => {
            if (!floorsByVenueId[floor.venueId]) {
                floorsByVenueId[floor.venueId] = [];
            }
            floorsByVenueId[floor.venueId].push(floor);
        });

        // Fill in the UI only venue.floors property
        filterOptions.venues.forEach(venue => {
            venue.floors = floorsByVenueId[venue.id] || [];
        });

        // Gather all departments by floor ID
        const collectDepartments = (items: { floorId: string; departments: string[] }[]): void => {
            items.forEach(item => {
                if (!departmentsByFloorId[item.floorId]) {
                    departmentsByFloorId[item.floorId] = new Set();
                }
                item.departments.forEach(department => departmentsByFloorId[item.floorId].add(department));
            });
        };

        collectDepartments(filterOptions.workspaces);
        collectDepartments(filterOptions.rooms);

        // Assign collected departments to each floor
        filterOptions.floors.forEach(floor => {
            floor.departments = Array.from(departmentsByFloorId[floor.id] || []);
        });
    }

    public static IncludeRooms(filter: ReservationInsightsFilter): boolean {
        return filter.workspaceOrAndRoom?.value === "both" || filter.workspaceOrAndRoom?.value === "rooms";
    }

    public static IncludeWorkplaces(filter: ReservationInsightsFilter): boolean {
        return filter.workspaceOrAndRoom?.value === "both" || filter.workspaceOrAndRoom?.value === "workplaces";
    }

    public static getDayOfWeekOptions(): IReactSelectValue[] {
        const dayOptions: IReactSelectValue[] = [];
        const daysOfWeek = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];

        daysOfWeek.forEach((day, index) => {
            dayOptions.push({
                label: day,
                value: `${index}`
            });
        });

        return dayOptions;
    }

    public static getHolidayOptions(): IReactSelectValue[] {
        const holidayOptions: IReactSelectValue[] = [];
        const holidays = [
            { label: 'Nieuwjaarsdag', value: "newYearsDay" },
            { label: 'Goede Vrijdag', value: "goodFriday" },
            { label: 'Eerste Paasdag', value: "firstEasterDay" },
            { label: 'Tweede Paasdag', value: "secondEasterDay" },
            { label: 'Koningsdag', value: "kingsDay" },
            { label: 'Hemelvaart', value: "ascensionDay" },
            { label: 'Eerste Pinksterdag', value: "whitSunday" },
            { label: 'Tweede Pinksterdag', value: "whitMonday" },
            { label: 'Eerste Kerstdag', value: "firstChrismasDay" },
            { label: 'Tweede Kerstdag', value: "secondChrismasDay" }
        ];

        holidays.forEach((holiday) => {
            holidayOptions.push({
                label: holiday.label,
                value: holiday.value
            });
        });

        return holidayOptions;
    }

    public static getWeekOptions(startDate: Date, endDate: Date): IReactSelectValue[] {
        const weekOptions: IReactSelectValue[] = [];

        let localStartDate = new Date(startDate);
        localStartDate.setHours(localStartDate.getHours() + 6); // Add 6 hours to prevent DST issues
        localStartDate.setDate(localStartDate.getDate() - (localStartDate.getDay() === 0 ? 6 : localStartDate.getDay() - 1));

        while (localStartDate <= endDate) {
            const weekStart = new Date(localStartDate);
            const weekEnd = new Date(localStartDate);
            weekEnd.setDate(localStartDate.getDate() + 6);

            const weekNumber = this.getISOWeekNumber(weekStart);
            const year = weekStart.getFullYear();

            if (weekStart <= endDate && weekEnd >= startDate) {
                const startDateString = weekStart.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: '2-digit' });
                const endDateString = weekEnd.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: '2-digit' });

                weekOptions.push({
                    label: `wk ${weekNumber} '24 (${startDateString} - ${endDateString})`,
                    value: `${year}${weekNumber}`
                });
            }

            localStartDate.setDate(localStartDate.getDate() + 7);
        }

        return weekOptions;
    }

    static getISOWeekNumber(date: Date): number {
        const tempDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
        tempDate.setUTCDate(tempDate.getUTCDate() + 4 - (tempDate.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
        const weekNumber = Math.ceil((((tempDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
        return weekNumber;
    }
}